import { cn } from "@/lib/utils";
import { useInViewOnce } from "@/hooks/useInViewOnce";
import GradientText from "@/components/home/primitives/GradientText";
import SectionHeading from "@/components/home/primitives/SectionHeading";
import { BRAND_MASCOT } from "@/data/brandMascots";

const BLUE = "#4E9CFF";
const PINK = "#FF4DB8";
const AMBER = "#FFBB33";

const SOURCES = ["Manuals", "FAQs", "Spreadsheets", "Website", "SOPs", "Pricing"];
const DOORWAYS = [
  "Voice",
  "Chat",
  "Phone",
  "QR codes",
  "Website",
  "Mobile",
  "Email",
  "Outbound",
];
const ACTIONS = [
  "Book appointments",
  "Update your CRM",
  "Check availability",
  "Create tickets",
  "Sync your database",
];

const Chip = ({
  label,
  index,
  show,
}: {
  label: string;
  index: number;
  show: boolean;
}) => (
  <span
    style={{ transitionDelay: show ? `${index * 45}ms` : "0ms" }}
    className={cn(
      "rounded-full border border-[#E5EAF0] bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm",
      "transition-all duration-500 ease-out hover:-translate-y-0.5 hover:border-genie-primary/40 hover:shadow-[0_6px_20px_-6px_rgba(78,156,255,0.45)]",
      show ? "translate-y-0 scale-100 opacity-100" : "translate-y-2 scale-95 opacity-0"
    )}
  >
    {label}
  </span>
);

/** Action pill — a live gradient dot marks it as something the genie *does*, not just a channel it talks on. */
const ActionChip = ({
  label,
  index,
  show,
}: {
  label: string;
  index: number;
  show: boolean;
}) => (
  <span
    style={{ transitionDelay: show ? `${index * 45}ms` : "0ms" }}
    className={cn(
      "inline-flex items-center gap-2 rounded-full border border-genie-accent/40 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm",
      "transition-all duration-500 ease-out hover:-translate-y-0.5 hover:border-genie-accent hover:shadow-[0_6px_20px_-6px_rgba(255,187,51,0.5)]",
      show ? "translate-y-0 scale-100 opacity-100" : "translate-y-2 scale-95 opacity-0"
    )}
  >
    <span className="h-2 w-2 flex-shrink-0 rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] animate-pulse motion-reduce:animate-none" />
    {label}
  </span>
);

/** A connector with a light beam that travels down it — knowledge flowing in, answers flowing out. */
const FlowConnector = ({
  to,
  delay = "0s",
}: {
  to: string;
  delay?: string;
}) => (
  <div className="relative my-1.5 flex h-12 w-8 justify-center" aria-hidden="true">
    {/* faint static rail */}
    <div
      className="h-full w-px"
      style={{ background: `linear-gradient(to bottom, ${to}33, ${to}55)` }}
    />
    {/* traveling light beam */}
    <div className="absolute inset-0 flex justify-center overflow-hidden">
      <span
        className="absolute top-0 h-8 w-px animate-beam-down motion-reduce:hidden"
        style={{
          animationDelay: delay,
          background: `linear-gradient(to bottom, transparent, ${to}, transparent)`,
          boxShadow: `0 0 10px 1px ${to}`,
        }}
      />
    </div>
  </div>
);

export default function HomeOneBrain() {
  const { ref, inView } = useInViewOnce<HTMLDivElement>();

  return (
    <section className="w-full bg-white py-16 sm:py-20 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="The mental model"
          title={
            <>
              One genie. <GradientText>Every doorway.</GradientText>
            </>
          }
          subtitle="Train it once on everything your business knows. Reach customers and staff wherever they already are. And it doesn't just answer, it takes action in the tools you already run."
        />

        <div
          ref={ref}
          className={cn(
            "mt-14 flex flex-col items-center transition-all duration-700 ease-out",
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          )}
        >
          {/* Sources */}
          <p className="text-xs font-semibold uppercase tracking-wide text-genie-primary">
            Trained on everything you know
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2.5 max-w-2xl">
            {SOURCES.map((s, i) => (
              <Chip key={s} label={s} index={i} show={inView} />
            ))}
          </div>

          {/* Connector — knowledge flowing in */}
          <FlowConnector to={PINK} delay="0s" />

          {/* One genie node */}
          <div className="relative">
            {/* rotating conic aura */}
            <div
              className="absolute -inset-6 -z-10 rounded-full opacity-40 blur-2xl animate-spin-slow motion-reduce:animate-none"
              style={{
                background: `conic-gradient(from 0deg, ${BLUE}, ${PINK}, ${AMBER}, ${BLUE})`,
              }}
            />
            {/* breathing core glow */}
            <div className="absolute inset-0 -z-10 rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] blur-2xl animate-breathe motion-reduce:animate-none" />
            <div className="p-[2px] rounded-[28px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-xl">
              <div className="rounded-[26px] bg-white px-8 py-6 text-center flex flex-col items-center">
                {/* The one genie itself — summoned into the source-of-truth node */}
                <div className="relative flex items-center justify-center">
                  <div className="absolute h-20 w-20 rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] opacity-20 blur-xl" />
                  <img
                    src={BRAND_MASCOT}
                    alt="The Help Genie mascot"
                    className="relative h-24 w-24 object-contain animate-bounce-subtle drop-shadow-[0_10px_22px_rgba(78,156,255,0.28)]"
                  />
                </div>
                <h3 className="mt-2 text-xl font-bold text-[#222B45]">One genie</h3>
                <p className="mt-1 text-sm text-gray-500 max-w-[16rem]">
                  Trained on what your business knows. One source of truth.
                </p>
              </div>
            </div>
          </div>

          {/* Connector — answers flowing out */}
          <FlowConnector to={BLUE} delay="0.4s" />

          {/* Doorways — where it answers */}
          <p className="text-xs font-semibold uppercase tracking-wide text-genie-secondary">
            Answering on every channel
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2.5 max-w-3xl">
            {DOORWAYS.map((d, i) => (
              <Chip key={d} label={d} index={i} show={inView} />
            ))}
          </div>

          {/* Connector into the action tier */}
          <FlowConnector to={AMBER} delay="0.8s" />

          {/* Actions — where it gets things done */}
          <p className="text-xs font-semibold uppercase tracking-wide text-genie-accent-dark">
            And taking action in your systems
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2.5 max-w-2xl">
            {ACTIONS.map((a, i) => (
              <ActionChip key={a} label={a} index={i} show={inView} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
